import { type UserGroup } from '#app/__generated__/api.js';
import { useHasUserGroups } from '#app/hooks/useHasUserGroups.js';

type UseHasOnboardedValue = {
  /**
   * Indicates if the current user has onboarded as a client.
   */
  hasOnboardedAsClient: boolean;
  /**
   * Indicates if the current user has onboarded as an independent.
   */
  hasOnboardedAsIndependent: boolean;
  /**
   * Indicates if the current user has onboarded as EITHER a client, independent, or both.
   */
  hasOnboardedAtAll: boolean;
};

/**
 * Provides information on if the current user (by default) has onboarded.
 * This includes booleans for if the user has onboarded at all,
 * if they have onboarded as a client, and if they have onboarded as an independent.
 *
 * Optionally you may pass a UserGroup array to check against a specific UserAccount.
 *
 * Be careful to use the right boolean for your use case.
 *
 * This is NOT the same as asking if the user has completed their profile. See `useHasCompletedProfile`.
 */
export const useHasOnboarded = (
  specificUserGroups?: readonly UserGroup[],
): UseHasOnboardedValue => {
  const hasOnboardedAsClient = useHasUserGroups(
    'CLIENT_ONBOARDING_COMPLETE',
    specificUserGroups,
  );
  const hasOnboardedAsIndependent = useHasUserGroups(
    'CONTRACTOR_ONBOARDING_COMPLETE',
    specificUserGroups,
  );

  return {
    hasOnboardedAsClient,
    hasOnboardedAsIndependent,
    hasOnboardedAtAll: hasOnboardedAsClient || hasOnboardedAsIndependent,
  };
};
